import { format, parse as parseDate, differenceInSeconds } from 'date-fns';
import parseHtml from 'html-react-parser';
import { openUrl } from './index';
import { isEmptyString } from './validation';
import { isAndroid, isIOS, sendSms, downloadFile, outLink } from './device';

/* Board 정보 */
export const BOARD_INFO = {
  notice: {
    type: 'notice',
    boardCode: 'A01',
  },
  manual: {
    type: 'board',
    boardCode: 'C01',
  },
  variable: {
    type: 'board',
    boardCode: 'D01',
  },
  document: {
    type: 'board',
    boardCode: 'B01',
  },
  'notice-management': {
    type: 'notice',
    boardCode: 'A01',
  },
  'notice-popup-management': {
    type: 'notice',
    boardCode: 'A02',
  },
  'manual-management': {
    type: 'board',
    boardCode: 'C01',
  },
  'variable-management': {
    type: 'board',
    boardCode: 'D01',
  },
  'document-management': {
    type: 'board',
    boardCode: 'B01',
  },
};

/* 파일 다운로드 공유 하는 URL Host */
export const BOARD_FILE_SHARE_HOST = `${window.location.origin}/gateway/chn-sfa/api/bbs/file-share`;
export const NOTICE_FILE_SHARE_HOST = loginSno =>
  `${window.location.origin}/gateway/chn-sfa/api/bbs/file-share-login/${loginSno}`;

/* Board에서 사용하는 날짜 형식 */
export const BOARD_DATE_FORMAT = 'yyyyMMdd';

/* 공지사항 팝업 게시판 코드 */
export const BOARD_NOTICE_CODE = 'A01';
export const BOARD_NOTICE_POPUP_CODE = 'A02';

/* 변액자료 게시판 코드 */
export const BOARD_VARIABLE_CODE = 'D01';

/* 게시판 채널 '전체' 코드 */
export const BOARD_CHANNEL_CODE_ALL = 'Z000';

/* 게시판 검색 기간 리스트 */
export const BOARD_PERIOD_TYPES = [
  { name: '일간', value: 'day' },
  { name: '주간', value: 'week' },
  { name: '월간', value: 'month' },
  { name: '연간', value: 'year' },
];

/* 게시판 검색 조건 리스트 */
export const BOARD_SEARCH_CONDITIONS = [
  { name: '내용', value: 'C' },
  { name: '등록자', value: 'R' },
  { name: '제목', value: 'T' },
];

/* 게시판 타이틀 Max Length */
export const BOARD_TITLE_MAX_LENGTH = 33;

/* 게시판 내용 Max Length */
export const BOARD_CONTENT_MAX_LENGTH = 1300;

/* 게시판 업로드 파일 용량 제한 */
export const BOARD_FILE_SIZE_LIMIT = 50 * 1024 * 1024;

/* 게시판 파일명 길이(Byte) 제한 */
export const BOARD_FILENAME_BYTE_LIMIT = 82;

/* 게시판 업로드 가능 파일 확장자 이름 목록 */
export const BOARD_ACCEPT_FILES = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'doc',
  'docx',
  'hwp',
  'pdf',
  'ppt',
  'pptx',
  'txt',
  'xlsx',
  'xls',
  'zip',
];

/* 게시판 업로드 가능 파일 확장자 목록 */
export const fileInputAccept = BOARD_ACCEPT_FILES.map(ext => `.${ext}`).join(
  ', ',
);

/**
 * Pagination을 위해 pageIndex와 한 번에 노출 될 pageCount개수를 바탕으로 현재 pageIndicatorIndex를 구하는 함수
 * @param {number} pageIndex 현재 Page Index
 * @param {number} viewPageCount Page Indicator에서 한 번에 노출 될 page의 개수
 * @return {number} 계산된 pageIndicator
 * @author Robin
 */
export const getIndicatorIndex = (pageIndex, viewPageCount) =>
  Math.floor(pageIndex / viewPageCount);

/**
 * targetRow를 구하는 함수
 * @param {object} pagingInfo 페이징 정보
 * @return {number} 계산된 targetRow
 * @author Robin
 */
export const getTargetRow = pagingInfo => {
  const { pageIndex, rowSize } = pagingInfo;
  return Math.max(pageIndex * rowSize + 1, 1);
};

/**
 * 게시판 리스트 호출 시의 기본 Parameter 를 구하는 함수
 * @param {string} boardCode 게시판의 코드
 * @return {object}
 * @author Robin
 */
export const getBoardDefaultParams = (boardCode, pagingInfo) => {
  const targetRow = getTargetRow(pagingInfo);
  return {
    bbsClssCd: boardCode,
    targetRow,
  };
};

/**
 * 게시물의 번호를 구하는 함수
 * @param {object} pagingInfo 페이징 정보
 * @param {number} postIndex 게시물의 Index
 * @return {object}
 * @author Robin
 */
export const getPostNumber = (pagingInfo, postIndex) => {
  const totalCount = pagingInfo.totalCount;
  const targetRow = getTargetRow(pagingInfo);
  return totalCount - (targetRow + postIndex) + 1;
};

/**
 * 게시글 목록을 조회하여 리턴하는 함수
 * @param {object} params 게시글 조회 시 사용할 Parameter
 * @param {function} getPostsApi API을 담당할 함수
 * @return {array} API 호출 결과로 조회된 게시글 목록
 * @author Robin
 */
export const fetchPosts = async (params, getPostsApi) => {
  const { data } = await getPostsApi({ params });

  const result = data?.pagingResult || {};
  const postInfos = fp.cloneDeep(result.list) || [];
  const { rows: totalCount = 0, targetRow } = result;

  return { postInfos, totalCount, targetRow };
};

/**
 * API에서 조회된 게시글의 format을 프론트에서 보여지는 format으로 변경하는 함수
 * @param {object} postInfo format을 맞출 게시글 정보
 * @return {object} 프론트 format에 맞춰진 게시글 정보
 * @author Robin
 */
export const formatPost = postInfo => {
  const formattedPost = Object.keys(postInfo).reduce((newPostInfo, key) => {
    // HTML Escape가 포함된 내용을 Unescape
    if (key === 'bbsContn') newPostInfo[key] = convertToHtmlText(postInfo[key]);
    // 신분 열람 정보 [=> 열람 가능 채널로 변경됨]
    else if (key === 'bbsAuthList')
      newPostInfo[key] = postInfo[key].map(({ bbsOrgChnlCd }) => bbsOrgChnlCd);
    // 채널 정보 세팅
    else if (key === 'bbsOrgChnlCd')
      newPostInfo['bbsOrgChnlCdReq'] = postInfo[key];
    else newPostInfo[key] = postInfo[key];
    return newPostInfo;
  }, {});

  return formattedPost;
};

/**
 * 파일 목록 중, 새로 업로드를 해야하는 파일 / 서버에서 받아온 파일을 구분해 필터링 하는 함수
 * @param {array} files 파일 목록
 * @param {'upload' | 'post'} type 업로드용 파일 / 서버에서 받아온 게시물 파일
 * @return {array} 필터된 파일들의 새로운 Array
 * @author Robin
 */
export const filterFiles = (files, type = 'upload') =>
  files.filter(file =>
    type === 'post' ? file.atchFileSno : !file.atchFileSno,
  );

/**
 * 파일 업로드를 위해 input[type=file]에서 받은 file을 blob으로 변환해주는 함수
 * @param {object} file Input 창에서 사용자가 선택한 file
 * @return {Promise} file을 blob 형태로 변환 처리하여 Blob을 리턴하는 Promise
 * @author Robin
 */
export const getFileBlob = file => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = async e => {
      try {
        const fileData = e.target.result;
        const fileRes = await fetch(fileData);
        const fileBlob = await fileRes.blob();

        resolve(fileBlob);
      } catch (error) {
        console.log('getFileBlob() - error', error);
        reject(error);
      }
    };
  });
};

/**
 * blob 타입을 파일로 다운로드
 * @param {Blob} blob Blob 형태의 데이터 파일
 * @param {string} filename 다운로드 할 파일명
 * @author Robin
 */
export const downloadBlob = (blob, filename) => {
  const fileUrl = URL.createObjectURL(blob);

  if (isAndroid) {
    downloadFile(fileUrl);
  } else if (isIOS) {
    outLink(fileUrl);
  } else {
    downloadUrl(fileUrl, filename);
  }

  URL.revokeObjectURL(fileUrl);
};

/**
 * 파일 URL 다운로드
 * @param {string} fileUrl 다운로드 할 파일 URL
 * @param {string} filename 다운로드 할 파일명
 * @author Robin
 */
export const downloadUrl = (fileUrl, filename) => {
  if (!fileUrl) return;
  filename = filename ? filename : getFilename(fileUrl);

  const a = document.createElement('a');
  a.href = fileUrl;
  a.download = filename;
  a.click();
};

/**
 * 파일 URL 에서 파일 이름 추출하는 함수
 * @param {string} fileUrl 파일 URL
 * @author Robin
 */
export const getFilename = fileUrl => {
  const filenameText = fileUrl.split('/').pop();
  const filename = filenameText.slice(0, filenameText.indexOf('?') + 1);
  return filename;
};

/**
 * 서버에서 파일 다운로드 API로 전달 받은 파일명을 header의 'content-disposition' 값에서 추출하는 함수
 * @param {string} headerText header의 content-disposition 텍스트
 * @return {string} 서버에서 받아온 파일명
 * @author Robin
 */
export const getFilenameFromHeader = headerText => {
  const FILENAME_SPLIT_START = 'filename="';
  const FILENAME_SPLIT_END = '";';
  const regExp = new RegExp(
    `(${FILENAME_SPLIT_START}\\S+${FILENAME_SPLIT_END})`,
  );
  const filteredText = headerText.match(regExp)?.shift();
  const encodedFilename = filteredText.slice(
    FILENAME_SPLIT_START.length,
    FILENAME_SPLIT_END.length * -1,
  );
  return decodeURIComponent(encodedFilename);
};

/**
 * 파일명 Byte 구하는 함수
 * @param {string} filename 파일명
 * @return {number} 파일명의 Byte
 * @author Robin
 */
export const getFilenameByte = filename => {
  let bts, ii, chr;
  for (
    bts = ii = 0;
    (chr = filename.charCodeAt(ii++));
    bts += chr >> 11 ? 3 : chr >> 7 ? 2 : 1
  );
  return bts;
};

/**
 * 한글/영문의 파일명 Byte 허용 최대값 구하는 구하는 함수
 * @param {string} fileExtension 파일 확장자
 * @return {object} 한글/영문의 Byte 최대값 정보 Object
 * @author Robin
 */
export const getFilenameLimitInfo = (fileExtension = '') => {
  const KOREAN_BYTE = 3,
    ENGLISH_BYTE = 1;

  const fileExtensionLength = getFilenameByte(`.${fileExtension}`);
  const filenameByteLimit = BOARD_FILENAME_BYTE_LIMIT - fileExtensionLength;
  const korean = Math.floor(filenameByteLimit / KOREAN_BYTE);
  const english = Math.floor(filenameByteLimit / ENGLISH_BYTE);

  return { korean, english };
};

/**
 * Array에서 특정 Index의 항목(들)을 제거해 새로운 Array를 리턴해주는 함수
 * @param {array} arr 항목을 제거할 원본 Array
 * @param {number | array} index 제거할 Index / 제거할
 * @return {array} 특정 index 한 개 혹은 / 여러개가 제거 된 새로운 Array
 * @author Robin
 */
export const removeIndex = (arr = [], index) => {
  try {
    if (!Array.isArray(arr))
      throw new Error('첫 번째 파라미터가 배열이 아닙니다.');

    const newArr = [...arr];

    if (typeof index === 'number') {
      newArr.splice(index, 1);
      return newArr;
    } else if (Array.isArray(index)) {
      return newArr.filter((_, i) => !index.includes(i));
    } else {
      throw '아무것도 제거 되지 않음 (두 번째 파라미터를 확인해주세요)';
    }
  } catch (error) {
    console.error('removeIndex() error - ', error);
  }
};

/**
 * 작성 텍스트에 HTML Tag가 포함되어 있는지 체크하는 함수
 * @param {string} text 작성된 텍스트
 * @return {boolean} HTML Tag 포함 여부
 * @author Robin
 */
export const checkHtmlTag = text => {
  const HTML_TAG_LIST = ['style', 'script', 'div', 'p', 'span'];

  const endTags = text.match(/<\/+[a-zA-Z0-9]+>/gi);
  if (endTags) {
    console.log('An end tag is included - endTags', endTags);
    return true;
  }

  const inlineTags = text.match(/<[a-zA-Z0-9]|\/>/gi);
  if (inlineTags) {
    console.log('An inline tag is included - inlineTags', inlineTags);
    return true;
  }

  const tagsText = HTML_TAG_LIST.join('|');
  const tagRegex = new RegExp(`<(${tagsText})>?(\\s|>)`, 'gi');
  const tags = tagsText.match(tagRegex);

  if (tags) {
    console.log('Some tag is included - tags', tags);
    return true;
  }

  return false;
};

/**
 * HTML을 Text 변환해주는 함수
 * @param {string} value HTML String
 * @return {string} 변환된 String
 * @author Robin
 */
export const escapeHtml = value => {
  if (!isEmptyString(value)) {
    value = String(value);

    value = value
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/';/g, '&#39;')
      .replace(/&/g, '&amp;');
  }

  return value;
};

/**
 * Text를 HTML로 변환해주는 함수
 * @param {string} value Text String
 * @return {string} 변환된 String
 * @author Robin
 */
export const unescapeHtml = value => {
  if (!isEmptyString(value)) {
    value = String(value);

    value = value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  return value;
};

/**
 * 텍스트에 포함된 줄바꿈 Escape 문자(\n)를 <br/>로 변경해주는 함수
 * @param {string} text 작성된 텍스트
 * @return {string} 변환된 Text
 * @author Robin
 */
export const convertToLineBreaker = text =>
  typeof text === 'string' ? text.replace(/\n/g, '<br/>') : '';

/**
 * 텍스트를 HTML 형태로 변환해주는 함수
 * @param {string} text 작성된 텍스트
 * @return {string} 변환된 Text
 * @author Robin
 */
export const convertToHtml = text =>
  parseHtml(convertToLineBreaker(unescapeHtml(text)) || '');

/**
 * 텍스트를 HTML Text 형태로 변환해주는 함수
 * @param {string} text 작성된 텍스트
 * @return {string} 변환된 Text
 * @author Robin
 */
export const convertToHtmlText = text => parseHtml(unescapeHtml(text) || '');

/**
 * 게시판의 Date String 값을 Board에서 사용하는 Date Format에 맞는 Date String으로 변환하는 함수
 * @param {string | Date} dateString 파싱할 Date String 혹은 Date 인스턴스
 * @param {string} dateFormat Date Format
 * @return {string} Format 된 Date String
 * @author Robin
 */
export const formatBoardDate = (dateString, dateFormat = BOARD_DATE_FORMAT) => {
  try {
    const date = new Date(dateString);
    return format(date, dateFormat);
  } catch (error) {
    console.log('formatBoardDate() - error', error);
    return '';
  }
};

/**
 * 게시판의 Date String 값을 Date 객체로 Parsing 하는 함수
 * @param {string} dateString 파싱할 맞출 Date String
 * @param {string} dateFormat Date Format
 * @return {Date} Parsing 된 Date 객체
 * @author Robin
 */
export const parseBoardDate = (dateString, dateFormat = BOARD_DATE_FORMAT) => {
  try {
    return parseDate(dateString, dateFormat, new Date());
  } catch (error) {
    console.log('parseBoardDate() - error', error);
    return '';
  }
};

/**
 * 게시판의 Date String 값을 String / Date를 판별하여, Date String으로 리턴하는 함수
 * @param {string | Date} dateString 파싱할 Date String 혹은 Date 인스턴스
 * @param {string} dateFormat 변경될 Date Format
 * @param {string} parseDateFormat 현재 파싱할 Date Format
 * @return {string} Format 된 Date String
 * @author Robin
 */
export const convertBoardDate = (
  dateString,
  dateFormat = BOARD_DATE_FORMAT,
  parseDateFormat = BOARD_DATE_FORMAT,
) => {
  try {
    const parsedDate = parseBoardDate(dateString, parseDateFormat);
    return formatBoardDate(parsedDate, dateFormat);
  } catch (error) {
    console.log('convertBoardDate() - error', error);
    return '';
  }
};

/**
 * 두 날짜의 시작 날짜와 종료 날짜가 유효한지 판단
 * @param {string} startDate 시작 날짜의 Date String
 * @param {string} endDate 종료 날짜의 Date String
 * @param {string} dateFormat Date의 Format
 * @return {boolean} 유효한 기간인지 여부
 * @author Robin
 */
export const checkValidPeriod = (
  startDate,
  endDate,
  dateFormat = BOARD_DATE_FORMAT,
) => {
  try {
    const start = parseDate(startDate, dateFormat, new Date());
    const end = parseDate(endDate, dateFormat, new Date());
    const distance = differenceInSeconds(end, start);
    const isValidPeriod = distance >= 0;

    return isValidPeriod;
  } catch (error) {
    console.log('checkValidPeriod() - error', error);
    return false;
  }
};

/**
 * 게시판 코드로 공지사항(공지사항 팝업 포함) 게시판인지 확인하는 함수
 * @param {string} boardCode 게시판 코드
 * @return {boolean} 공지사항/공지사항 팝업 인지 여부
 * @author Robin
 */
export const checkNotice = boardCode =>
  boardCode === BOARD_NOTICE_CODE || boardCode === BOARD_NOTICE_POPUP_CODE;

/**
 * 카카오 톡으로 URL을 공유하는 함수
 * @param {string} fileUrl 공유할 파일 URL
 * @author Robin
 */
export const shareKakao = fileUrl => {
  Kakao.Share.sendDefault({
    objectType: 'text',
    text: fileUrl,
    link: {
      mobileWebUrl: fileUrl,
      webUrl: fileUrl,
    },
  });
};

/**
 * SMS로 내용을 공유하는 함수 (Web URL Schema 사용)
 * @param {string} fileUrl 공유할 파일 URL
 * @author Robin
 * @deprecated
 */
export const shareSms = fileUrl => {
  let smsUrl = isIOS
    ? `sms:&body=${window.encodeURIComponent(fileUrl)}`
    : `sms:?body=${window.encodeURIComponent(fileUrl)}`;

  if (smsUrl) openUrl(smsUrl, 'outLink');
};

/**
 * SMS로 내용을 공유하는 함수 (Mobile Interface 함수 사용)
 * @param {string} fileUrl 공유할 파일 URL
 * @author Robin
 */
export const shareSmsMobile = fileUrl => {
  sendSms(fileUrl);
};
